import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Trash2, RefreshCw, X, Search, Package, 
  BarChart3, Building2, ChevronDown, ChevronUp, Award 
} from 'lucide-react';
import TenantMobileLayout from './TenantMobileLayout';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

// Formattazione valuta italiana
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '€0,00';
  return new Intl.NumberFormat('it-IT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 3
  }).format(value);
};

const TenantMobileMagazzino = ({ onBack, getHeaders }) => {
  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newProdotto, setNewProdotto] = useState({
    nome: '', categoria: '', unita_misura: 'kg', prezzo_acquisto: 0, fornitore: '', scorta_minima: 0, quantita: 0
  });

  // Tab: 'lista' o 'confronta'
  const [activeTab, setActiveTab] = useState('lista');
  
  // Ricerca avanzata
  const [advancedSearch, setAdvancedSearch] = useState('');
  const [advancedResults, setAdvancedResults] = useState([]);
  const [loadingAdvanced, setLoadingAdvanced] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});

  const loadProdotti = useCallback(async () => {
    try {
      let url = `${API}/gestione/tenant/prodotti?limit=200`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const response = await axios.get(url, { headers: getHeaders ? getHeaders() : {} });
      setProdotti(response.data.prodotti || []);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, [search, getHeaders]);

  useEffect(() => { 
    if (activeTab === 'lista') {
      loadProdotti(); 
    }
  }, [loadProdotti, activeTab]);

  // Ricerca avanzata con confronto prezzi
  const searchAdvancedProducts = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setAdvancedResults([]);
      return;
    }
    
    setLoadingAdvanced(true);
    try {
      const params = new URLSearchParams();
      params.append('search', searchTerm);
      params.append('limit', '100');
      
      const res = await axios.get(`${API}/magazzino/prodotti/cerca?${params}`, {
        headers: getHeaders ? getHeaders() : {}
      });
      
      setAdvancedResults(res.data.prodotti || []);
    } catch (error) {
      console.error('Errore ricerca avanzata:', error);
      toast.error('Errore nella ricerca prodotti');
    } finally {
      setLoadingAdvanced(false);
    }
  };

  // Debounce ricerca avanzata
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'confronta' && advancedSearch.length >= 2) {
        searchAdvancedProducts(advancedSearch);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [advancedSearch, activeTab]);

  const toggleProductExpand = (productKey) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productKey]: !prev[productKey]
    }));
  };

  const handleAddProdotto = async () => {
    if (!newProdotto.nome) {
      toast.error('Inserisci il nome del prodotto');
      return;
    }
    try {
      await axios.post(`${API}/gestione/tenant/prodotti`, newProdotto, { headers: getHeaders ? getHeaders() : {} });
      toast.success('Prodotto aggiunto!');
      setShowAddModal(false);
      setNewProdotto({ nome: '', categoria: '', unita_misura: 'kg', prezzo_acquisto: 0, fornitore: '', scorta_minima: 0, quantita: 0 });
      loadProdotti();
    } catch (error) {
      toast.error("Errore nell'aggiunta");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    try {
      await axios.delete(`${API}/gestione/tenant/prodotti/${id}`, { headers: getHeaders ? getHeaders() : {} });
      toast.success('Prodotto eliminato');
      loadProdotti();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <TenantMobileLayout title="Magazzino" onBack={onBack}>
      {/* Tab Switcher */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('lista')}
          className={`flex-1 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'lista'
              ? 'bg-cyan-500 text-black'
              : 'bg-black/40 text-gray-400 border border-cyan-500/30'
          }`}
          data-testid="tab-lista"
        >
          <Package className="h-4 w-4" />
          Lista
        </button>
        <button
          onClick={() => setActiveTab('confronta')}
          className={`flex-1 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
            activeTab === 'confronta'
              ? 'bg-green-500 text-black'
              : 'bg-black/40 text-gray-400 border border-green-500/30'
          }`}
          data-testid="tab-confronta"
        >
          <BarChart3 className="h-4 w-4" />
          Confronta Prezzi
        </button>
      </div>

      {/* TAB LISTA PRODOTTI */}
      {activeTab === 'lista' && (
        <>
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Add Button */}
              <button 
                onClick={() => setShowAddModal(true)}
                className="w-full py-4 rounded-2xl border border-cyan-500/30 flex items-center justify-center gap-2 text-cyan-400 font-medium active:scale-98 transition-all"
                style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
              >
                <Plus className="h-5 w-5" />
                Nuovo Prodotto
              </button>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <input 
                  type="text"
                  placeholder="Cerca prodotti..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  data-testid="search-lista"
                />
              </div>

              {/* Products List */}
              {prodotti.length === 0 ? (
                <div 
                  className="text-center py-12 rounded-2xl border border-cyan-500/20"
                  style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
                >
                  <p className="text-gray-400">Nessun prodotto trovato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {prodotti.map(p => (
                    <div 
                      key={p.id} 
                      className={`rounded-2xl p-4 border ${p.quantita <= p.scorta_minima ? 'border-red-500/50' : 'border-cyan-500/20'}`}
                      style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-white truncate">{p.nome}</h3>
                          {p.categoria && <p className="text-xs text-cyan-400">{p.categoria}</p>}
                        </div>
                        <button 
                          onClick={() => handleDelete(p.id)} 
                          className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center ml-2"
                        >
                          <Trash2 className="h-4 w-4 text-red-400" />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-black/30 rounded-lg p-2">
                          <p className="text-gray-500 text-xs">Quantità</p>
                          <p className={`font-semibold ${p.quantita <= p.scorta_minima ? 'text-red-400' : 'text-white'}`}>
                            {p.quantita} {p.unita_misura}
                          </p>
                        </div>
                        <div className="bg-black/30 rounded-lg p-2">
                          <p className="text-gray-500 text-xs">Prezzo</p>
                          <p className="font-semibold text-cyan-400">{formatCurrency(p.prezzo_acquisto)}</p>
                        </div>
                      </div>
                      {p.fornitore && <p className="text-xs text-gray-500 mt-2 truncate">Fornitore: {p.fornitore}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* TAB CONFRONTA PREZZI */}
      {activeTab === 'confronta' && (
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-green-500" />
            <input 
              type="text"
              placeholder="Cerca prodotto (es: gambero, panna...)"
              value={advancedSearch}
              onChange={(e) => setAdvancedSearch(e.target.value)}
              className="w-full pl-12 pr-10 py-4 bg-black/40 border-2 border-green-500/40 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-lg"
              data-testid="search-confronta"
            />
            {advancedSearch && (
              <button 
                onClick={() => {
                  setAdvancedSearch('');
                  setAdvancedResults([]);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {advancedSearch.length >= 2 && (
            <p className="text-sm text-gray-400 text-center">
              {loadingAdvanced 
                ? 'Ricerca in corso...' 
                : `${advancedResults.length} prodotti trovati`
              }
            </p>
          )}

          {/* Results */}
          {loadingAdvanced ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-green-500" />
            </div>
          ) : advancedResults.length > 0 ? (
            <div className="space-y-3">
              {advancedResults.map((gruppo, idx) => (
                <div 
                  key={idx}
                  className="rounded-2xl border border-green-500/30 overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.3) 100%)' }}
                  data-testid={`result-${idx}`}
                >
                  {/* Header - clickable */}
                  <div 
                    className="p-4 active:bg-white/5 transition-colors"
                    onClick={() => toggleProductExpand(gruppo.nome_prodotto)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white">{gruppo.nome_prodotto}</h3>
                        <p className="text-xs text-gray-400 mt-1">
                          <Building2 className="h-3 w-3 inline mr-1" />
                          {gruppo.num_fornitori} fornitor{gruppo.num_fornitori > 1 ? 'i' : 'e'} · {gruppo.num_occorrenze} acquist{gruppo.num_occorrenze > 1 ? 'i' : 'o'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {expandedProducts[gruppo.nome_prodotto] ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Prezzo min/max summary */}
                    <div className="flex gap-3 mt-3">
                      <div className="flex-1 bg-green-500/20 rounded-lg p-2 text-center">
                        <p className="text-xs text-green-400">Min</p>
                        <p className="font-bold text-green-400">{formatCurrency(gruppo.prezzo_min)}</p>
                        <p className="text-xs text-green-400/70 truncate">{gruppo.fornitore_min}</p>
                      </div>
                      {gruppo.prezzo_max > gruppo.prezzo_min && (
                        <div className="flex-1 bg-red-500/20 rounded-lg p-2 text-center">
                          <p className="text-xs text-red-400">Max</p>
                          <p className="font-bold text-red-400">{formatCurrency(gruppo.prezzo_max)}</p>
                          <p className="text-xs text-red-400/70 truncate">{gruppo.fornitore_max}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Expanded details */}
                  {expandedProducts[gruppo.nome_prodotto] && (
                    <div className="border-t border-green-500/20 p-4 bg-black/30">
                      <h4 className="text-sm font-medium text-gray-300 mb-3">
                        Dettaglio fornitori (dal più economico)
                      </h4>
                      <div className="space-y-2">
                        {gruppo.occorrenze?.map((occ, occIdx) => (
                          <div 
                            key={occIdx}
                            className={`p-3 rounded-lg ${
                              occIdx === 0 
                                ? 'bg-green-500/20 border border-green-500/40' 
                                : occIdx === gruppo.occorrenze.length - 1 && gruppo.occorrenze.length > 1
                                  ? 'bg-red-500/20 border border-red-500/40'
                                  : 'bg-black/20 border border-gray-700'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {occIdx === 0 && <Award className="h-4 w-4 text-green-400" />}
                                  <p className="font-medium text-white">{occ.fornitore}</p>
                                </div>
                                <p className="text-xs text-gray-400 mt-1 line-clamp-2">{occ.descrizione}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {occ.data_fattura} · Qtà: {occ.quantita_fattura}
                                  {occ.pezzi_confezione > 1 && ` (${occ.pezzi_confezione} pz/conf)`}
                                </p>
                              </div>
                              <div className="text-right ml-2">
                                <p className={`text-lg font-bold ${
                                  occIdx === 0 
                                    ? 'text-green-400' 
                                    : occIdx === gruppo.occorrenze.length - 1 && gruppo.occorrenze.length > 1
                                      ? 'text-red-400'
                                      : 'text-white'
                                }`}>
                                  {formatCurrency(occ.prezzo_singolo_pezzo)}
                                </p>
                                <p className="text-xs text-gray-500">/pezzo</p>
                                {occ.pezzi_confezione > 1 && (
                                  <p className="text-xs text-gray-500">
                                    Conf: {formatCurrency(occ.prezzo_confezione)}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Risparmio potenziale */}
                      {gruppo.prezzo_max > gruppo.prezzo_min && (
                        <div className="mt-3 p-3 bg-blue-500/20 rounded-lg border border-blue-500/40">
                          <p className="text-sm text-blue-300">
                            <strong>Risparmio:</strong> {formatCurrency(gruppo.prezzo_max - gruppo.prezzo_min)}/pz 
                            ({((1 - gruppo.prezzo_min / gruppo.prezzo_max) * 100).toFixed(1)}%)
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : advancedSearch.length >= 2 ? (
            <div 
              className="text-center py-12 rounded-2xl border border-green-500/20"
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
            >
              <Search className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">Nessun prodotto trovato per "{advancedSearch}"</p>
              <p className="text-sm text-gray-500 mt-1">Prova con un termine diverso</p>
            </div>
          ) : (
            <div 
              className="text-center py-12 rounded-2xl border border-green-500/20"
              style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 100%)' }}
            >
              <BarChart3 className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">Cerca un prodotto per confrontare i prezzi</p>
              <p className="text-sm text-gray-500 mt-1">Minimo 2 caratteri</p>
            </div>
          )}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end justify-center">
          <div 
            className="w-full max-w-md rounded-t-3xl border-t border-cyan-500/30 max-h-[90vh] overflow-y-auto"
            style={{ background: 'linear-gradient(180deg, rgba(20,20,30,0.98) 0%, rgba(10,10,20,0.98) 100%)' }}
          >
            <div className="p-6 border-b border-cyan-500/20 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Nuovo Prodotto</h2>
              <button onClick={() => setShowAddModal(false)} className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Nome Prodotto</label>
                <input
                  type="text"
                  value={newProdotto.nome}
                  onChange={(e) => setNewProdotto({...newProdotto, nome: e.target.value})}
                  className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="Es: Pomodori San Marzano"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Categoria</label>
                  <input
                    type="text"
                    value={newProdotto.categoria}
                    onChange={(e) => setNewProdotto({...newProdotto, categoria: e.target.value})}
                    className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    placeholder="Es: Verdure"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Unità</label>
                  <select
                    value={newProdotto.unita_misura}
                    onChange={(e) => setNewProdotto({...newProdotto, unita_misura: e.target.value})}
                    className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="kg">kg</option>
                    <option value="pz">pz</option>
                    <option value="lt">lt</option>
                    <option value="gr">gr</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Quantità</label>
                  <input
                    type="number"
                    value={newProdotto.quantita}
                    onChange={(e) => setNewProdotto({...newProdotto, quantita: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Prezzo (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newProdotto.prezzo_acquisto}
                    onChange={(e) => setNewProdotto({...newProdotto, prezzo_acquisto: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Scorta Minima</label>
                <input
                  type="number"
                  value={newProdotto.scorta_minima}
                  onChange={(e) => setNewProdotto({...newProdotto, scorta_minima: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Fornitore</label>
                <input
                  type="text"
                  value={newProdotto.fornitore}
                  onChange={(e) => setNewProdotto({...newProdotto, fornitore: e.target.value})}
                  className="w-full px-4 py-3 bg-black/40 border border-cyan-500/30 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
              <button
                onClick={handleAddProdotto}
                className="w-full py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-black font-semibold rounded-xl shadow-lg shadow-cyan-500/30 active:scale-98 transition-all"
              >
                Salva Prodotto
              </button>
            </div>
          </div>
        </div>
      )}
    </TenantMobileLayout>
  );
};

export default TenantMobileMagazzino;
